import {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Capacitor } from "@capacitor/core";
import CurrentSession from "data/userSession";
import { APP_IDENTITY } from "config/app";

const AD_BANNER_HEIGHT = Capacitor.isNativePlatform() ? 66 : 50;

interface LayoutContextValue {
  /** Whether the bottom tab bar is shown (mobile/tablet only) */
  showTabBar: boolean;
  /** Whether we're on a mobile/tablet device */
  isMobile: boolean;
  /** Whether the ad/support banner is shown (non-supporters on any platform) */
  showAds: boolean;
  /** Height reserved for ads at the bottom */
  adBannerHeight: number;
  /** Whether user has an active tug session */
  hasActiveSession: boolean;
  /** Refresh the active session status */
  refreshActiveSession: () => void;
}

const LayoutContext = createContext<LayoutContextValue | null>(null);

interface LayoutProviderProps {
  children: ReactNode;
}

export const LayoutProvider = ({ children }: LayoutProviderProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [supporter, setSupporter] = useState(true);
  const [isDemoUser, setIsDemoUser] = useState(false);
  const showAds = !supporter || isDemoUser;
  const [hasActiveSession, setHasActiveSession] = useState(false);

  const checkActiveSession = useCallback(() => {
    // Read directly from user document
    const user = CurrentSession.user;
    setHasActiveSession(!!user?.active);
  }, []);

  // Check on mount and when auth/session state changes
  useEffect(() => {
    // Initial check - wait for user to be loaded first
    const initCheck = async () => {
      const user = await CurrentSession.load();
      setHasActiveSession(!!user?.active);
      setSupporter(!!user?.supporter);
      setIsDemoUser(user?.uid === APP_IDENTITY.demoUserId);
    };
    initCheck();

    const handleChange = () => {
      checkActiveSession();
    };

    // Listen for various events that could change the active session state
    window.addEventListener("authStateChanged", handleChange);
    window.addEventListener("tugSessionChanged", handleChange);
    window.addEventListener("userUpdated", handleChange);

    return () => {
      window.removeEventListener("authStateChanged", handleChange);
      window.removeEventListener("tugSessionChanged", handleChange);
      window.removeEventListener("userUpdated", handleChange);
    };
  }, [checkActiveSession]);

  const value = useMemo<LayoutContextValue>(
    () => ({
      showTabBar: isMobile,
      isMobile,
      showAds,
      adBannerHeight: showAds ? AD_BANNER_HEIGHT : 0,
      hasActiveSession,
      refreshActiveSession: checkActiveSession,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMobile, showAds, hasActiveSession, checkActiveSession, supporter]
  );

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
};

export const useLayout = (): LayoutContextValue => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayout must be used within a LayoutProvider");
  }
  return context;
};

export default LayoutContext;
